import React, { useState, useMemo, useEffect, useCallback, ReactElement } from 'react';
import { useLocation } from 'react-router';
import style from './index.scss';
import { useTranslations } from '@core/js/hooks/i18n';
import { useAppSelector, useGameConfigurationSelector } from '@core/js/store/hooks';
import { addParamsToUrl, getLocationParams } from '@core/js/utils/url.util';
import { WebContent } from '@core/js/components/system/WebContent';
import { prop, pipe, map, propOr, unnest, uniq, intersection, keys } from 'ramda';
import { storageService } from '@core/js/services/storage.service';
import { dispatcher } from 'dispatcher';
import { useLogout } from '@core/js/hooks/auth';
import { isStickyGamesBtnShown } from './constants';
import { useShowAgain } from './hooks/useShowAgain';

import { useDTAG } from '@theme/js/hooks/useDTAG';
import { IGame } from '@theme/js/hooks/games/types';
import { IGameFeedConfig } from '@theme/js/components/system/App/store/types';

export const StickyGamesButton = () => {
    const [open, setOpen] = useState<boolean>(false);
    const [availableGames, setAvailableGames] = useState<IGame[]>([]);
    const [isShowAgain, setIsShowAgain] = useShowAgain();
    const logout = useLogout();
    const games = useGameConfigurationSelector(prop('games'));
    const gamesFeeds = useAppSelector(prop('oldGamesFeeds'));
    const location = useLocation();
    const t = useTranslations('playtech.mobile.system.2FA.preferences.');

    const showStickyButton = useDTAG('DTAG_SHOW_STICKY_BUTTON')?.includes(location.pathname) || null;

    const clearChoiceByLogout = useCallback(() => {
        storageService.local.removeItem(isStickyGamesBtnShown);
    }, []);

    useEffect(() => {
        dispatcher.listen('auth:action:logout', clearChoiceByLogout);

        return () => {
            dispatcher.removeListener('auth:action:logout', clearChoiceByLogout);
        };
    }, [clearChoiceByLogout, isShowAgain, logout]);

    const stickyGamesFeed = useMemo(
        () => gamesFeeds.find((feed: IGameFeedConfig) => feed.name === 'StickyButtonGames'),
        [gamesFeeds]
    );
    const stickyGameCodes = useMemo(
        () => {
            if (stickyGamesFeed) {
                return pipe(
                    map(propOr([], 'gameCodes')),
                    unnest,
                    uniq
                )(stickyGamesFeed.categories) as string[];
            }
        },
        [stickyGamesFeed]
    );

    useEffect(() => {
        if (stickyGameCodes) {
            const availableGamesCode = intersection(keys(games), stickyGameCodes);
            setAvailableGames(availableGamesCode.map(el => games[el]));
        }
    }, [games, stickyGameCodes]);

    const togglePanel = () => setOpen(!open);
    const handleShowingAgainBtnClick = () => {
        storageService.local.setItem(isStickyGamesBtnShown, !isShowAgain);
        setIsShowAgain(false);
    };
    const renderGame = (): ReactElement[] => {
        // needs to render only 3 first games in feed
        const firstThreeGames: IGame[] = availableGames.slice(0, 3);

        return firstThreeGames.map((el) => {
            // TODO CODERE: Check if defaultGameImage fits correctly here
            const imageURL = addParamsToUrl(getLocationParams(), el.defaultGameImage);

            return (
                <div key={el.gameCode} className={`${style.gameItem} fn-sticky-button-game fn-launch-game`} data-game-code={el.gameCode}>
                    <a className={style.gameItemLink} href="#">
                        <img src={imageURL} className={style.gameItemImg} />
                    </a>
                </div>
            );
        });
    };

    return (
        <>
            {showStickyButton && availableGames && stickyGamesFeed && isShowAgain ?
                <div className={open
                    ? `${style.container} ${style.containerOpen}`
                    : `${style.container}`}>
                    <div className={open
                        ? `${style.content} ${style.contentOpen}`
                        : `${style.content}`}
                    >
                        <div className={open
                            ? `${style.contentItemOpen}`
                            : ''}
                        >   <div className={style.gameItemsContainer}>
                                {renderGame()}
                            </div>
                        </div>
                        <button className={style.notShowButton} onClick={handleShowingAgainBtnClick}>
                            <p>{t('playtech.mobile.system.DoNotShowAgain')}</p>
                        </button>
                    </div>
                    <div className={open
                        ? `${style.handler} ${style.handlerOpen}`
                        : `${style.handler}`} onClick={togglePanel}>{
                            open ?
                                <div className={style.closeBtn}> &times;</div> :
                                <WebContent
                                    className={style.logo}
                                    id={'STICKY_BUTTON_ICON'}
                                />
                        }
                    </div>
                </div> : null
            }
        </>
    );
};
